import { useRef, useState, useEffect } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import { useMediaQuery, Box } from '@mui/material';
import CompanyUpperBar from '../components/Company/UpperBar';
import MyAdvisors from '../scenes/MyAdvisors';
import SearchAdvisors from '../scenes/SearchAdvisors';
import SavedAdvisors from '../scenes/SavedAdvisors';
import TopMatchesAdvisors from '../scenes/TopMatchesAdvisors';
import { useAccount } from '../contexts/Account';
import ImpersonateUpperBar from '../components/ImpersonationUpperBar';
import CompanyDashboard from '../scenes/CompanyDashboard';
import IndividualPostEdit from '../scenes/IndividualPostEdit';
import { CompanyExtraFeedProvider } from '../contexts/CompanyFeed';
import { PostType } from '../services/utils/types';
import FreeTrialBanner from '../components/Company/FreeTrialBanner';
import ExFreemiumBanner from '../components/Company/ExFreemiumBanner';
import OnboardingBanner from '../components/Company/OnboardingBanner';
import IndividualPost from '../scenes/IndividualPost';
import CompanyRightMenu from '../components/Company/RightMenu/index';
import eventBus, { eventBusValues } from '../eventBus';
import CompanyAccount from '../scenes/CompanyAccount';
import Ideas from '../scenes/Ideas';
import BoardMeetings from '../scenes/BoardMeetings';
import DynamicIFrameScene from '../scenes/DynamicIFrameScene';
import CompanyDashboardV2 from '../scenes/CompanyDashboardV2';
import BoardActivities from '../scenes/BoardActivities';
import MobileBottomNav from '../components/TopBar/MobileBottomNav';
import { getCompanyActivity } from '../services/company_services';
import ACSnackbar from '../components/ACSnackbar';
import { ReactComponent as Home } from '../icons/home.svg';
import { ReactComponent as SupervisorAccount } from '../icons/supervisor_account.svg';
import { ReactComponent as Notifications } from '../icons/notifications.svg';
import { ReactComponent as BusinessCenter } from '../icons/business_center.svg';

function getTopValue(showSmallBanner) {
  if (showSmallBanner) {
    return '100px';
  }
  return '50px';
}

const useStyles = ({ showSmallBanner }) => ({
  companyContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 'auto',
  },
  viewContainer: {
    marginTop: getTopValue(showSmallBanner),
    overscrollBehavior: 'contain',
    display: 'flex',
    flex: 'auto',
    overflowX: 'hidden',
    height: `calc(100vh - ${getTopValue(showSmallBanner)})`,
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#F8F8F8',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '5px',
      backgroundColor: '#C7C7C7',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#B7B7B7',
    },
    '&::-webkit-scrollbar-thumb:active': {
      backgroundColor: '#B7B7B7',
      boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
    },
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    width: '100%',
  },
  companyContainerImpersonated: {
    display: 'flex',
    flexDirection: 'row',
    flex: 'auto',
    justifyContent: 'center',
  },
});

function BaseLayout() {
  const { isImpersonated, isFreeTrial, accountDetails, isFreemium } =
    useAccount();
  const showOnboardingBanner =
    !isFreeTrial &&
    accountDetails.opportunityStage === 'discovery' &&
    accountDetails.onboardingStatus !== 'Not Requisite' &&
    accountDetails.onboardingStatus !== 'Complete' &&
    !accountDetails.freemiumRegistration;
  const isSM = useMediaQuery((umq) => umq.breakpoints.down('sm'));
  const classes = useStyles({
    isSM,
    showSmallBanner: showOnboardingBanner,
  });
  const boxRef = useRef();
  const history = useHistory();
  const [activities, setActivities] = useState([]);
  const [activitiesSnackBarOpen, setActivitiesSnackBarOpen] = useState(false);

  useEffect(() => {
    getCompanyActivity({
      companyId: accountDetails.id,
      filterType: ['advisorJoinedToCompany', 'surveyResponse', 'advisorIdea'],
    })
      .then((response) => {
        setActivities(response.data.getCompanyActivity || []);
      })
      .catch((error) => {
        console.error('Error fetching company activity:', error);
      });
  }, []);

  const menuItems = [
    {
      label: 'Boardroom',
      to: '/dashboard',
      id: 'b2bNavBarBoardroom',
      icon: <Home style={{ width: '28px', height: '28px' }} />,
    },
    {
      label: 'Board Activity',
      shortLabel: 'Activity',
      onClick: () => {
        if (activities.length > 0) {
          history.push(
            '/board_activity?filter=advisorJoinedToCompany&filter=surveyCreated&filter=advisorIdea&filter=boardMeetingCreated'
          );
        } else {
          setActivitiesSnackBarOpen(true);
        }
      },
      paths: ['/board_activity'],
      id: 'b2bNavBarBoardActivity',
      icon: <Notifications style={{ width: '28px', height: '28px' }} />,
    },
    {
      label: 'Advisors',
      to: '/advisors',
      id: 'b2bNavBarAdvisors',
      icon: <SupervisorAccount style={{ width: '28px', height: '28px' }} />,
    },
    {
      label: 'Resources',
      shortLabel: 'Services',
      to: '/ub/advisory-board-resources/',
      id: 'b2bNavBarFAQs',
      icon: <BusinessCenter style={{ width: '28px', height: '28px' }} />,
    },
  ];

  return (
    <CompanyExtraFeedProvider>
      {isImpersonated && <ImpersonateUpperBar />}
      {isFreemium ? null : (
        <FreeTrialBanner show={isFreeTrial} isCompanyContact />
      )}
      {accountDetails.freemiumRegistration &&
        accountDetails.opportunityStage === 'discovery' &&
        !isFreemium && <ExFreemiumBanner show />}
      {showOnboardingBanner && <OnboardingBanner show />}
      <div
        style={
          isImpersonated
            ? classes.companyContainerImpersonated
            : classes.companyContainer
        }
      >
        <CompanyRightMenu
          onDismiss={() => {
            eventBus.dispatch(eventBusValues.mobileCompanyMenuTrigger);
          }}
          accountDetails={accountDetails}
        />

        <div style={classes.childrenContainer}>
          <CompanyUpperBar menuItems={menuItems} />
          <MobileBottomNav menuItems={menuItems} />

          <ACSnackbar
            open={activitiesSnackBarOpen}
            text="No Boardroom Activity Yet! Invite more advisors to your board."
            severity="success"
            onClose={() => setActivitiesSnackBarOpen(false)}
            autoHideDuration={5000}
          />

          <Box sx={classes.viewContainer} ref={boxRef}>
            <div style={{ width: '100%' }}>
              <Switch>
                <Route
                  path="/dashboard"
                  render={() => <CompanyDashboardV2 />}
                />
                <Route path="/messages" component={CompanyDashboard} />
                {!isFreemium && (
                  <Route
                    path="/board-meetings"
                    component={() => <BoardMeetings />}
                  />
                )}
                <Route path="/post/:postId?" component={IndividualPost} />
                <Route
                  path="/question/:postId?/edit"
                  component={() => (
                    <IndividualPostEdit postType={PostType.QUESTION} />
                  )}
                />
                <Route exact path="/advisor_ideas" component={Ideas} />
                <Route
                  path="/board_activity"
                  component={BoardActivities}
                />
                <Route
                  exact
                  path="/advisors"
                  component={() => <MyAdvisors boxRef={boxRef} />}
                />
                <Route
                  exact
                  path="/advisors/search"
                  component={() => <SearchAdvisors boxRef={boxRef} />}
                />
                <Route
                  exact
                  path="/advisors/saved"
                  component={() => <SavedAdvisors boxRef={boxRef} />}
                />
                <Route
                  exact
                  path="/advisors/top_matches"
                  component={() => <TopMatchesAdvisors boxRef={boxRef} />}
                />
                <Route
                  exact
                  path="/advisors/:publicProfileUrl"
                  component={MyAdvisors}
                />
                <Route exact path="/account" component={CompanyAccount} />
                <Route path="/ub" component={DynamicIFrameScene} />
                <Route path="/wf" component={DynamicIFrameScene} />
              </Switch>
            </div>
          </Box>
        </div>
      </div>
    </CompanyExtraFeedProvider>
  );
}

export default BaseLayout;
